// Map geometry. Every Valor world wraps (configs: map.allow_map_wrapping is true everywhere; world-process
// CoordUtils.GetDistance folds a delta larger than half the world). x runs east, y runs NORTH: the phone's map puts
// (x, y+1) above (x, y) (Map/MapChunkScript.cs positions chunks at (cx*64, cy*64) in Unity UI space, where y is up),
// and Ben's World 2 screenshots agree.

/** Wrap a coordinate into [0, size). */
export const wrap = (v: number, size: number) => ((v % size) + size) % size;

/** The shortest signed delta from a to b on a wrapping axis. */
export function wrapDelta(a: number, b: number, size: number): number {
  let d = b - a;
  if (d > size / 2) d -= size;
  else if (d < -size / 2) d += size;
  return d;
}

/** Distance in fields between two cells, the short way round (the server's CoordUtils.GetDistance). */
export function wrapDistance(a: [number, number], b: [number, number], size: number): number {
  const dx = Math.abs(a[0] - b[0]);
  const dy = Math.abs(a[1] - b[1]);
  const fx = dx > size / 2 ? size - dx : dx;
  const fy = dy > size / 2 ? size - dy : dy;
  return Math.hypot(fx, fy);
}

/** The continent number the game prints as "R563" — front-server Util/ContinentCalculator.php, step for step
 *  (it divides the coordinates by the number of continents per side, not by the continent length; (505,518) in a
 *  1024 world → R563, as the app shows). The game sends `continent` on every village it describes, so this is only for
 *  cells it hasn't told us about yet. */
export function continentOf(x: number, y: number, size: number, continentLength = 100): number {
  const perSide = Math.ceil(size / continentLength);
  const cx = Math.ceil(x / perSide);
  const cy = Math.ceil(y / perSide);
  return (cy - 1) * perSide + cx;
}

/** The map view: the cell under the centre of the canvas (fractional, wrapping) and the scale in CSS px per cell. */
export interface View {
  cx: number;
  cy: number;
  /** CSS px per cell */
  s: number;
}

/** A cell CORNER → canvas CSS px (y flipped: north is up). Cell (x, y) spans corners (x, y) [south-west] to
 *  (x + 1, y + 1) [north-east], so its top-left on screen is cellToPx(x, y + 1). */
export function cellToPx(view: View, w: number, h: number, size: number, x: number, y: number): [number, number] {
  const dx = wrapDelta(view.cx, x, size);
  const dy = wrapDelta(view.cy, y, size);
  return [w / 2 + dx * view.s, h / 2 - dy * view.s];
}

/** The CENTRE of cell (x, y) on the canvas. */
export function cellCentrePx(view: View, w: number, h: number, size: number, x: number, y: number): [number, number] {
  const dx = wrapDelta(view.cx, x + 0.5, size);
  const dy = wrapDelta(view.cy, y + 0.5, size);
  return [w / 2 + dx * view.s, h / 2 - dy * view.s];
}

/** Canvas CSS px → fractional cell coordinates (wrapped). */
export function pxToCell(view: View, w: number, h: number, size: number, px: number, py: number): [number, number] {
  return [wrap(view.cx + (px - w / 2) / view.s, size), wrap(view.cy - (py - h / 2) / view.s, size)];
}

/** The integer cell range (inclusive, unwrapped) the canvas shows, with a margin of cells. */
export function visibleCells(view: View, w: number, h: number, margin = 1): { x0: number; x1: number; y0: number; y1: number } {
  const halfW = w / 2 / view.s;
  const halfH = h / 2 / view.s;
  return {
    x0: Math.floor(view.cx - halfW) - margin,
    x1: Math.ceil(view.cx + halfW) + margin,
    y0: Math.floor(view.cy - halfH) - margin,
    y1: Math.ceil(view.cy + halfH) + margin,
  };
}

/** A small, fast, deterministic hash of a cell (for scenery jitter). Returns [0, 1). */
export function cellHash(x: number, y: number, salt: number): number {
  let h = (Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(salt | 0, 2246822519)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296;
}
